import { useState } from 'react';
import { api } from '../../services/api';
import toast from '../Toast';
import type { ModelConfigData } from './types';

interface ModelFormProps {
  model: ModelConfigData | null;
  onClose: () => void;
  onSuccess: () => void;
  onConfigureRouting: (model: ModelConfigData) => void;
}

export default function ModelForm({ model, onClose, onSuccess, onConfigureRouting }: ModelFormProps) {
  const [form, setForm] = useState({
    modelId: model?.modelId || '',
    name: model?.name || '',
    description: model?.description || '',
    standardCredits: model?.standardCredits ?? 1,
    hdCredits: model?.hdCredits ?? 2,
    uhdCredits: model?.uhdCredits ?? 3,
    text2imgCredits: model?.text2imgCredits ?? 0,
    img2imgCredits: model?.img2imgCredits ?? 0,
    inpaintCredits: model?.inpaintCredits ?? 0,
    isActive: model?.isActive ?? true,
    sortOrder: model?.sortOrder ?? 0,
    enableText2img: model?.enableText2img ?? true,
    enableImg2img: model?.enableImg2img ?? true,
    enableInpaint: model?.enableInpaint ?? true,
    appendPrompt: model?.appendPrompt || '',
    enableStandardSize: model?.enableStandardSize ?? true,
    enableHdSize: model?.enableHdSize ?? true,
    enableUhdSize: model?.enableUhdSize ?? true,
    enableTransparentBackground: model?.enableTransparentBackground ?? false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        description: form.description || null,
        appendPrompt: form.appendPrompt || null,
      };

      if (model) {
        await api.updateModel(model.id, payload);
      } else {
        await api.createModel(payload);
      }
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || '保存失败');
    }
  };

  const numberValue = (value: string) => parseInt(value, 10) || 0;
  const inputClass = 'w-full rounded-xl border border-light-border bg-white px-4 py-3 text-light-text outline-none transition focus:border-accent-blue focus:ring-4 focus:ring-blue-50 disabled:cursor-not-allowed disabled:bg-light-hover disabled:text-light-muted';
  const labelClass = 'mb-1.5 block text-sm font-medium text-light-text';
  const sectionClass = 'rounded-2xl border border-light-border bg-white p-5 shadow-sm';
  const modeCards = [
    ['enableText2img', 'text2imgCredits', '文生图'],
    ['enableImg2img', 'img2imgCredits', '图生图'],
    ['enableInpaint', 'inpaintCredits', '局部重绘'],
  ] as const;

  const SectionHeader = ({ title, desc, badge }: { title: string; desc: string; badge?: string }) => (
    <div className="mb-5 flex items-start justify-between gap-4">
      <div>
        <h4 className="text-sm font-semibold text-light-text">{title}</h4>
        <p className="mt-1 text-xs leading-5 text-light-muted">{desc}</p>
      </div>
      {badge && <span className="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-accent-blue">{badge}</span>}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="flex max-h-[calc(92vh-150px)] min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="grid gap-5 xl:grid-cols-[0.95fr_1.05fr]">
          <div className="space-y-5">
            <section className={sectionClass}>
              <SectionHeader title="模型身份" desc="定义站内模型与前台展示信息。" badge="核心" />
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>模型ID</label>
                  <input
                    type="text"
                    value={form.modelId}
                    onChange={(e) => setForm({ ...form, modelId: e.target.value })}
                    placeholder="站内业务模型ID，例如 gpt-image-2-enhanced"
                    className={inputClass}
                    required
                  />
                  <p className="mt-1 text-xs text-light-muted">作为站内唯一 ID，用于选择、计费、路由和任务记录。</p>
                </div>

                <div className="grid gap-4 md:grid-cols-[1fr_120px]">
                  <div>
                    <label className={labelClass}>显示名称</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputClass}
                      required
                    />
                  </div>
                  <div>
                    <label className={labelClass}>排序</label>
                    <input
                      type="number"
                      value={form.sortOrder}
                      onChange={(e) => setForm({ ...form, sortOrder: numberValue(e.target.value) })}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>描述</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <div>
                  <label className={labelClass}>附加提示词</label>
                  <textarea
                    value={form.appendPrompt}
                    onChange={(e) => setForm({ ...form, appendPrompt: e.target.value })}
                    rows={4}
                    className={`${inputClass} resize-none`}
                    placeholder="保存后会自动拼接在用户提示词后面"
                  />
                  <p className="mt-1 text-xs text-light-muted">实际请求时会按“用户提示词 + 换行 + 附加提示词”发送给上游模型。</p>
                </div>
              </div>
            </section>
          </div>

          <div className="space-y-5">
            <section className={sectionClass}>
              <SectionHeader title="路由预览" desc="渠道命中规则已拆分到独立弹窗，不再和积分配置混在一起。" />
              <div className="space-y-4">
                <div className="rounded-2xl border border-light-border bg-light-hover/30 p-4">
                  <p className="text-sm font-medium text-light-text">
                    {model
                      ? `当前已配置 ${model.routingSummary?.defaultRuleCount ?? 0} 条默认规则，${model.routingSummary?.overrideCount ?? 0} 条分辨率覆盖`
                      : '新模型需先保存，保存后才能配置独立路由'}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-light-muted">
                    路由规则已独立为单独弹窗，支持全局默认、模式默认，以及文生图/图生图/局部重绘在 1K、2K、4K 下的单独覆盖。
                  </p>
                  <div className="mt-4">
                    <button
                      type="button"
                      disabled={!model}
                      onClick={() => model && onConfigureRouting(model)}
                      className="rounded-xl border border-accent-blue px-4 py-2 text-sm font-medium text-accent-blue transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-light-border disabled:text-light-muted"
                    >
                      配置独立路由
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <SectionHeader title="发布状态与模式能力" desc="控制模型是否发布，以及各生成模式是否开放和对应的附加积分。" />
              <div className="space-y-4">
                <label className="flex items-center justify-between gap-3 rounded-xl border border-light-border bg-light-hover/30 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-light-text">启用模型</p>
                    <p className="mt-1 text-xs text-light-muted">关闭后模型不会在前台提供给用户选择。</p>
                  </div>
                    <input
                      type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="h-4 w-4 rounded border-light-border"
                  />
                </label>

                <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
                  {modeCards.map(([enabledKey, creditsKey, label]) => (
                    <div key={enabledKey} className="rounded-xl border border-light-border bg-light-hover/30 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <label className="text-sm font-medium text-light-text">{label}</label>
                        <label className="flex items-center gap-1.5 text-xs text-light-muted">
                          <input
                            type="checkbox"
                            checked={form[enabledKey as keyof typeof form] as boolean}
                            onChange={(e) => setForm({ ...form, [enabledKey]: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-light-border"
                          />
                          启用
                        </label>
                      </div>
                      <input
                        type="number"
                        min="0"
                        value={form[creditsKey as keyof typeof form] as number}
                        onChange={(e) => setForm({ ...form, [creditsKey]: numberValue(e.target.value) })}
                        disabled={!(form[enabledKey as keyof typeof form] as boolean)}
                        className={inputClass}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <SectionHeader title="分辨率与基础积分" desc="控制输出规格是否开放，以及不同规格对应的基础消耗。" />
              <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
                {[
                  ['standardCredits', 'enableStandardSize', '标准(1K)'],
                  ['hdCredits', 'enableHdSize', '高清(2K)'],
                  ['uhdCredits', 'enableUhdSize', '超清(4K)'],
                ].map(([creditsKey, enabledKey, label]) => (
                  <div key={creditsKey} className="rounded-xl border border-light-border bg-light-hover/30 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <label className="text-sm font-medium text-light-text">{label}</label>
                      <label className="flex items-center gap-1.5 text-xs text-light-muted">
                        <input
                          type="checkbox"
                          checked={form[enabledKey as keyof typeof form] as boolean}
                          onChange={(e) => setForm({ ...form, [enabledKey]: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-light-border"
                        />
                        启用
                      </label>
                    </div>
                    <input
                      type="number"
                      min="0"
                      value={form[creditsKey as keyof typeof form] as number}
                      onChange={(e) => setForm({ ...form, [creditsKey]: numberValue(e.target.value) })}
                      disabled={!(form[enabledKey as keyof typeof form] as boolean)}
                      className={inputClass}
                    />
                  </div>
                ))}
              </div>
              <label className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-light-border bg-light-hover/30 p-3">
                <span>
                  <span className="block text-sm font-medium text-light-text">支持透明背景</span>
                  <span className="block text-xs text-light-muted">开启后首页生成页显示"背景"选项，可产出透明背景 PNG（要求渠道支持 gpt-image 系列协议）</span>
                </span>
                <input
                  type="checkbox"
                  checked={form.enableTransparentBackground}
                  onChange={(e) => setForm({ ...form, enableTransparentBackground: e.target.checked })}
                  className="h-3.5 w-3.5 rounded border-light-border"
                />
              </label>
            </section>
          </div>
        </div>
      </div>

      <div className="mt-5 flex shrink-0 items-center justify-between border-t border-light-border bg-light-bg pt-4">
        <p className="text-xs text-light-muted">保存后配置会立即影响新的生成任务。</p>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-light-muted transition-colors hover:bg-light-hover hover:text-light-text">
            取消
          </button>
          <button type="submit" className="rounded-xl bg-accent-blue px-6 py-2 font-medium text-white transition-all hover:shadow-lg">
            保存
          </button>
        </div>
      </div>
    </form>
  );
}
